/* eslint-disable max-lines */
import orderBy from 'lodash.orderby'
import {observer} from 'mobx-react-lite'
import React, {UIEvent, useEffect, useMemo, useRef, useState} from 'react'
import useResizeObserver from 'use-resize-observer'
import {Lang} from 'yougile-lang'
import {ArrUtil, TypeUtil, Util} from 'yougile-util'
import {
  DataObjectId,
  getFieldsFromReport,
  getStickersFromTasks,
  Report,
  ReportDataField,
  ReportFieldKey,
  ReportSort,
  TaskData
} from 'yougile-model'
import {Sync} from '@client/sync'
import {FlatLoader} from '@components/common/base/flat-loader/flat-loader'
import {Loader} from '@components/common/base/loader'
import {DivWithHint} from '@components/common/hint/hint'
import {useIdTaskUpdater} from '@components/id-tasks/use-id-task-updater'
import {Overlay} from '@components/overlay'
import {useDragScrollable} from '@containers/drag-scrollable'
import {
  noPropagate,
  useBoolean,
  useForceUpdate,
  useLoadedLazyTaskEffect,
  useLocalStorage,
  usePreviousDeep,
  useReportTasksIds,
  useSyncEvent,
  useTasksDataFromIds
} from '@containers/hooks'
import {
  DragControl,
  dragPropsFromCtl,
  useDraggable
} from '@containers/hooks-draggable'
import {cn} from '@containers/html-utils'
import {getAllStickers} from '@extensions/sticker/sticker-data-extractor'
import {makeTaskDataService} from '@extensions/task-data-helper'
import CollapseAllIcon from './collapse-all.svg'
import ExpandAllIcon from './expand-all.svg'
import {SubtasksInReport} from './subtasks-in-report/subtasks-in-report'

const pageSize = 100

const getDefaultColumnWidth = (fieldKey: ReportFieldKey) => {
  const defaultColumnWidth = 240
  if (fieldKey === 'title') {
    return defaultColumnWidth * 2
  }
  return defaultColumnWidth * 0.7
}

interface Props {
  report: Report
  update: (updater: (report: Report) => Report) => void
  tasks?: TaskData[]
}

interface InnerProps {
  tasks: TaskData[]
  allFields: ReportDataField[]
}

interface State {
  page: number
  sortIx: number
  sortAsc: boolean
}

const getColumnWidthKey = (reportId: DataObjectId) =>
  'report-column-width-' + reportId

const Resize = (props: {
  fillerRef: React.RefObject<HTMLDivElement>
  elementRef: React.RefObject<HTMLTableHeaderCellElement>
  onMouseDownChange: (mouseDown: boolean) => void
  onMouseMove: (movementX: number) => void
}) => {
  const bem = Util.bemFrom('stats-reports-table')
  const mouseDownRef = useRef(false)
  const setMouseDown = (mouseDown: boolean) => {
    mouseDownRef.current = mouseDown
    props.onMouseDownChange(mouseDown)
  }
  const setFillerWidth = (width: number) => {
    props.fillerRef.current.style.width = width + 'px'
  }
  const stateRef = useRef({
    initialPageX: 0,
    initialFillerWidth: 0,
    initialWidth: 0
  })
  useEffect(() => {
    const mouseUpHandler = () => {
      setMouseDown(false)
    }
    const mouseMoveHandler = (e: MouseEvent) => {
      if (mouseDownRef.current) {
        const offset = e.pageX - stateRef.current.initialPageX
        props.onMouseMove(stateRef.current.initialWidth + offset)
        if (offset < 0) {
          setFillerWidth(stateRef.current.initialFillerWidth - offset)
        }
      }
    }
    document.addEventListener('mouseup', mouseUpHandler)
    document.addEventListener('mousemove', mouseMoveHandler)
    return () => {
      document.removeEventListener('mouseup', mouseUpHandler)
      document.removeEventListener('mousemove', mouseMoveHandler)
    }
  }, [props.elementRef])
  return (
    <div
      onClick={e => e.stopPropagation()}
      onMouseDown={e => {
        setMouseDown(true)
        stateRef.current = {
          initialPageX: e.pageX,
          initialFillerWidth: props.fillerRef.current.clientWidth,
          initialWidth: props.elementRef.current.clientWidth
        }
        e.stopPropagation()
      }}
      className={bem('resize')}
    />
  )
}

const Paging = ({
  maxPage,
  page,
  next,
  prev
}: {
  maxPage: number
  page: number
  next: () => void
  prev: () => void
}) => {
  const bem = Util.bemFrom('stats-reports-table')
  return (
    <div className={bem('paging')}>
      {maxPage > 1 && (
        <>
          <span className={bem('page')}>
            {Lang.msg.statsPage}: <b>{page + 1}</b> {Lang.msg.of} {maxPage}
          </span>
          <div className={bem('prev', {disable: page <= 0})} onClick={prev}>
            « {Lang.msg.statsPrev}
          </div>
          <div className={bem('delim')} />
          <div
            className={bem('next', {disable: page + 1 >= maxPage})}
            onClick={next}
          >
            {Lang.msg.statsNext} »
          </div>
        </>
      )}
    </div>
  )
}

const updateFieldPositions = (
  index: number,
  toIndex: number,
  allFields: ReportDataField[],
  sortIx: number,
  sortAsc: boolean,
  setAllFields: (value: ReportDataField[]) => void,
  setSortIx: (value: ((prevState: number) => number) | number) => void
) => {
  return (report: Report) => {
    if (index === undefined || index === toIndex || index === toIndex - 1) {
      return report
    }
    const fields = allFields.map(x => x(Lang.msg).key)
    const sortField = fields[sortIx]

    const dragField = fields[index]
    const newFields = ArrUtil.insert(
      ArrUtil.deletedAtIndex(fields, index),
      index < toIndex ? toIndex - 1 : toIndex,
      dragField
    )
    const sortReport: ReportSort = {key: sortField, asc: sortAsc}
    const newSort = report.columns[0].sort
      ? ArrUtil.changedAtIndex(report.columns[0].sort, sortReport, 0)
      : [sortReport]
    const newColumns = ArrUtil.changedAtIndex(
      report.columns,
      {...report.columns[0], fields: newFields, sort: newSort},
      0
    )
    setAllFields(orderBy(allFields, x => newFields.indexOf(x(Lang.msg).key)))
    const newSortIx = newFields.indexOf(sortField)
    setSortIx(newSortIx)
    return {...report, columns: newColumns}
  }
}

export const DropZone = ({
  allFields,
  ix,
  columnWidths,
  dragCtl
}: {
  allFields: ReportDataField[]
  ix: number
  columnWidths: Record<string, number>
  dragCtl: DragControl<number>
}) => {
  const bem = Util.bemFrom('stats-reports-table')
  const fieldKey = allFields[ix](Lang.msg).key
  const width = (columnWidths[fieldKey] ?? getDefaultColumnWidth(fieldKey)) / 2
  return dragCtl.draggedId !== undefined ? (
    <>
      <div
        className={bem('drop-zone', {
          left: true,
          leftMarker: dragCtl.dragOverId === ix
        })}
        style={{width}}
        {...dragPropsFromCtl(ix, dragCtl)}
      />
      <div
        {...dragPropsFromCtl(ix + 1, dragCtl)}
        style={{width}}
        className={bem('drop-zone', {
          right: true,
          rightMarker:
            dragCtl.dragOverId === ix + 1 &&
            dragCtl.dragOverId === allFields.length
        })}
      />
    </>
  ) : null
}

interface TableParams {
  isAtTop: boolean
  topWithReserve: number
  bottomWithReserve: number
  allFields: ReportDataField[]
  elementsRef: React.MutableRefObject<{
    [p: string]: React.RefObject<HTMLTableHeaderCellElement>
  }>
  sortIx: number
  resizeColumnIndex: number
  sortAsc: boolean
  dragCtl: DragControl<number>
  columnWidths: Record<string, number>
  headerClick: (ix: number) => () => void
  resizeFillerRef: React.MutableRefObject<HTMLDivElement | null>
  setResizeColumnIndex: (value: number | undefined) => void
  updateColumnWidth: (fieldKey: string, width: number) => void
  tasksToRender: TaskData[]
  sortedTasks: TaskData[]
}

// eslint-disable-next-line max-lines-per-function
const ReportTable = ({
  isAtTop,
  topWithReserve,
  bottomWithReserve,
  allFields,
  elementsRef,
  sortIx,
  resizeColumnIndex,
  sortAsc,
  dragCtl,
  columnWidths,
  headerClick,
  resizeFillerRef,
  setResizeColumnIndex,
  updateColumnWidth,
  tasksToRender,
  sortedTasks
}: TableParams) => {
  const toggleAllSubtasks = useBoolean(true)
  const bem = Util.bemFrom('stats-reports-table')
  const {ref, height = 1} = useResizeObserver()

  const timeTrackingCellKeys = ['timetrackingPlan', 'timetrackingWork']

  const showSumTitle = allFields.some(field => {
    const cellField = field(Lang.msg)
    return (
      cellField.getType?.() === 'numeric' ||
      timeTrackingCellKeys.includes(cellField.key)
    )
  })

  return (
    <table className="w-auto ml-8 table-fixed">
      <thead
        className={cn('sticky top-0 z-10', !isAtTop && 'shadow-bottom-old')}
      >
        <tr>
          {allFields.map(
            // eslint-disable-next-line max-lines-per-function
            (f, ix) => {
              const fieldKey = f(Lang.msg).key
              if (!elementsRef.current[fieldKey]) {
                elementsRef.current[fieldKey] =
                  React.createRef<HTMLTableHeaderCellElement>()
              }
              const elementRef = elementsRef.current[fieldKey]
              return (
                <th
                  ref={elementRef}
                  className={bem('head-cell', {
                    first: ix === 0,
                    sorted: ix === sortIx,
                    resize: resizeColumnIndex === ix,
                    asc: sortAsc
                  })}
                  draggable={true}
                  {...dragPropsFromCtl(ix, dragCtl)}
                  style={{
                    minWidth:
                      columnWidths[fieldKey] ?? getDefaultColumnWidth(fieldKey),
                    width:
                      columnWidths[fieldKey] ?? getDefaultColumnWidth(fieldKey),
                    maxWidth:
                      columnWidths[fieldKey] ?? getDefaultColumnWidth(fieldKey)
                  }}
                  key={ix}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={headerClick(ix)}
                >
                  <DropZone
                    allFields={allFields}
                    ix={ix}
                    columnWidths={columnWidths}
                    dragCtl={dragCtl}
                  />
                  <div
                    className={cn(fieldKey === 'title' && 'flex items-center')}
                  >
                    {fieldKey === 'title' && (
                      <DivWithHint
                        className="mr-24 text-secondary-old hover:text-action-hover"
                        onClick={e => {
                          noPropagate(e)
                          toggleAllSubtasks.toggle()
                        }}
                        content={
                          toggleAllSubtasks.value
                            ? Lang.msg.statsReportsTable.collapseAllSubtasks
                            : Lang.msg.statsReportsTable.expandAllSubtasks
                        }
                      >
                        {toggleAllSubtasks.value ? (
                          <CollapseAllIcon />
                        ) : (
                          <ExpandAllIcon />
                        )}
                      </DivWithHint>
                    )}
                    {f(Lang.msg).getName()}
                  </div>
                  <Resize
                    fillerRef={resizeFillerRef}
                    onMouseDownChange={mouseDown => {
                      setResizeColumnIndex(mouseDown ? ix : undefined)
                    }}
                    elementRef={elementRef}
                    onMouseMove={width => {
                      updateColumnWidth(fieldKey, width)
                    }}
                  />
                </th>
              )
            }
          )}
        </tr>
        {showSumTitle && (
          <tr>
            {allFields.map((f, ix) => {
              const name = f(Lang.msg).getType?.()
              const key = f(Lang.msg).key

              const stickerIsNumeric = name === 'numeric'
              const stickerIsTimeTracking = timeTrackingCellKeys.includes(key)

              const stickerId = stickerIsNumeric ? key.split('sticker:')[1] : ''

              const sum =
                stickerIsNumeric || stickerIsTimeTracking
                  ? sortedTasks.reduce((acc, task) => {
                      if (stickerIsTimeTracking) {
                        const taskDataService = makeTaskDataService(
                          Sync.get(task)
                        )
                        return (
                          acc +
                          Number(f(Lang.msg).getText(task, taskDataService))
                        )
                      }
                      const taskStickerValue = task.stickers[stickerId]
                      return taskStickerValue
                        ? Number((acc + Number(taskStickerValue)).toFixed(4))
                        : acc
                    }, 0)
                  : 0

              return (
                <th className={bem('head-cell', 'sum-title')}>
                  {ix === 0 && (
                    <div
                      className={bem('sum-title', {
                        titleField: key === 'title'
                      })}
                    >
                      {Lang.msg.totalValueByColumn}
                      {(stickerIsNumeric || stickerIsTimeTracking) &&
                        `: ${sum}`}
                    </div>
                  )}
                  {ix !== 0 && (stickerIsNumeric || stickerIsTimeTracking) && (
                    <div className={bem('sum-title-value')}>{sum}</div>
                  )}
                </th>
              )
            })}
          </tr>
        )}
      </thead>
      <tbody ref={ref}>
        <SubtasksInReport
          tasksToRender={tasksToRender}
          allFields={allFields}
          resizeColumnIndex={resizeColumnIndex}
          columnWidths={columnWidths}
          dragCtl={dragCtl}
          toggleAll={toggleAllSubtasks.value}
          tbodyHeight={height}
          topWithReserve={topWithReserve}
          bottomWithReserve={bottomWithReserve}
          getDefaultColumnWidth={getDefaultColumnWidth}
        />
      </tbody>
    </table>
  )
}

// eslint-disable-next-line max-lines-per-function
const StatsReportsTableBase = (props: Props & InnerProps & State) => {
  const [onTop, setOnTop] = useState(true)
  const [topWithReserve, setTopWithReserve] = useState(0)
  const [bottomWithReserve, setBottomWithReserve] = useState(0)
  const draggedState = useRef(false)
  const {tasks, update, report} = props
  const [resizeColumnIndex, setResizeColumnIndex] = useState<
    number | undefined
  >(undefined)

  const [page, setPage] = useState(props.page)
  const [sortAsc, setSortAsc] = useState(props.sortAsc)

  const [allFields, setAllFields] = useState(props.allFields)
  const [sortIx, setSortIx] = useState(props.sortIx)
  const [columnWidths, setColumnWidths] = useLocalStorage<
    Record<string, number>
  >(getColumnWidthKey(report.id), {})

  useEffect(() => {
    setSortIx(props.sortIx)
  }, [props.sortIx])

  useEffect(() => {
    setSortAsc(props.sortAsc)
  }, [props.sortAsc])

  const updateColumnWidth = (fieldKey: string, width: number) => {
    if (width <= 0) {
      return
    }
    setColumnWidths(x => {
      return {
        ...x,
        [fieldKey]: width
      }
    })
  }

  const virtualizationCalc = (top: number, bottom: number) => {
    const reserve = bottom - top
    const topWithReserve = top - reserve
    const bottomWithReserve = bottom + reserve
    setTopWithReserve(topWithReserve)
    setBottomWithReserve(bottomWithReserve)
  }

  useEffect(() => {
    const top = ref.current?.scrollTop ?? 0
    const bottom = top + (ref.current?.offsetHeight ?? 0)
    virtualizationCalc(top, bottom)
  }, [])

  useEffect(() => {
    setAllFields(props.allFields)
  }, [props.allFields])

  const bem = Util.bemFrom('stats-reports-table')
  const from = page * pageSize
  const to = (page + 1) * pageSize
  const prev = () => setPage(page - 1)
  const next = () => setPage(page + 1)
  const count = tasks.length
  const maxPage = Math.ceil(count / pageSize)

  const [ref, mouseDown] = useDragScrollable({direction: 'scroll-xy'})

  const sortedTasks = [...tasks]
  sortedTasks.sort(
    (a, b) => allFields[sortIx]?.(Lang.msg)?.order(a, b, !sortAsc) ?? 0
  )
  const tasksToRender = sortedTasks.slice(from, to)
  const resizeFillerRef = useRef<HTMLDivElement | null>(null)

  const headerClick = (ix: number) => () => {
    setSortAsc(sortIx === ix ? !sortAsc : false)
    setSortIx(ix)
  }

  const dragCtl = useDraggable<number>({
    onDrop: (index, toIndex) => {
      update(
        updateFieldPositions(
          index,
          toIndex,
          allFields,
          sortIx,
          sortAsc,
          setAllFields,
          setSortIx
        )
      )
    }
  })
  const elementsRef = useRef(
    Object.fromEntries(
      allFields.map(f => [
        f(Lang.msg).key,
        React.createRef<HTMLTableHeaderCellElement>()
      ])
    )
  )

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    if (!draggedState.current) {
      const top = (e.target as HTMLDivElement).scrollTop
      const bottom = top + (e.target as HTMLDivElement).offsetHeight
      virtualizationCalc(top, bottom)
      setOnTop(top === 0)
    }
  }

  const mouseUp = () => {
    document.removeEventListener('mouseup', mouseUp)
    draggedState.current = false
    const top = ref.current.scrollTop
    const bottom = top + ref.current.offsetHeight
    virtualizationCalc(top, bottom)
    setOnTop(top === 0)
  }

  const onMouseDown = (e: React.MouseEvent<Element, MouseEvent>) => {
    if (
      e.nativeEvent.offsetX > (e.target as HTMLDivElement).clientWidth ||
      e.nativeEvent.offsetY > (e.target as HTMLDivElement).clientHeight
    ) {
      draggedState.current = false
    } else {
      draggedState.current = true
      mouseDown(e)
      document.addEventListener('mouseup', mouseUp)
    }
  }

  return (
    <div
      className={bem()}
      onMouseDown={onMouseDown}
      ref={ref}
      onScroll={onScroll}
    >
      {resizeColumnIndex !== undefined && (
        <Overlay className="cursor-col-resize" />
      )}
      <div className="flex">
        <ReportTable
          isAtTop={onTop}
          topWithReserve={topWithReserve}
          bottomWithReserve={bottomWithReserve}
          allFields={allFields}
          elementsRef={elementsRef}
          sortIx={sortIx}
          resizeColumnIndex={resizeColumnIndex}
          sortAsc={sortAsc}
          dragCtl={dragCtl}
          columnWidths={columnWidths}
          headerClick={headerClick}
          resizeFillerRef={resizeFillerRef}
          setResizeColumnIndex={setResizeColumnIndex}
          updateColumnWidth={updateColumnWidth}
          tasksToRender={tasksToRender}
          sortedTasks={sortedTasks}
        />
        <div style={{width: 100}} ref={resizeFillerRef} className="shrink-0">
          &nbsp;
        </div>
      </div>

      <Paging maxPage={maxPage} page={page} next={next} prev={prev} />
    </div>
  )
}

export const StatsReportsTable = observer((props: Props) => {
  const {report} = props
  const column = report.columns[0]
  const box = Sync.getCurrentSandbox()

  const {
    data: tasksIds,
    isLoading: areTasksLoading,
    isFetching: areTasksFetching
  } = useReportTasksIds({
    report,
    onlyForTasks: props.tasks,
    shouldUpdateOnDataChange: true
  })
  const taskData = useTasksDataFromIds(tasksIds)
  const taskHubs = useMemo(
    () => taskData.map(task => box?.getHub(task.id)).filter(TypeUtil.isTruthy),
    [taskData]
  )

  useLoadedLazyTaskEffect(taskHubs, 'stats-reports-table')

  const allStickers = getAllStickers(box, true)

  const forceUpdate = useForceUpdate()

  useIdTaskUpdater()
  usePreviousDeep(props)

  useSyncEvent(
    'current',
    {filter: '*', debounceTime: 2000},
    () => {
      forceUpdate()
    },
    [forceUpdate]
  )

  const allFields = getFieldsFromReport(
    column,
    getStickersFromTasks(taskData, allStickers, column.fields)
  )

  const sortIxFromReport =
    column.sort && column.sort.length > 0
      ? allFields.findIndex(
          f => f(Lang.msg).key === column.sort?.slice(-1)[0].key
        )
      : -1

  const sortAsc =
    sortIxFromReport === -1 ? false : column.sort?.slice(-1)[0].asc || false
  const sortIx = sortIxFromReport === -1 ? 0 : sortIxFromReport

  if (areTasksLoading) {
    return (
      <div className="flex justify-center">
        <Loader />
      </div>
    )
  }

  const Base = StatsReportsTableBase
  return (
    <>
      {areTasksFetching && (
        <div className="relative">
          <div className="absolute top-[-3px] left-0 right-0">
            <FlatLoader />
          </div>
        </div>
      )}
      <Base
        {...props}
        page={0}
        sortIx={sortIx}
        sortAsc={sortAsc}
        tasks={taskData}
        allFields={allFields}
      />
    </>
  )
})
